import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import { Link } from "react-router-dom";
import { Calculator, HelpCircle, ArrowRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const img = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=800`;

const TERMS = [
  {
    id: "zakat", title: "Zakat", tag: "The Third Pillar",
    image: img("1512632578888-169bbbc64f33"),
    text: "Zakat is the third pillar of Islam — an obligatory act of worship that purifies wealth. Every eligible Muslim gives 2.5% of their qualifying net wealth held above the Nisab threshold for one lunar year. It is not merely charity, but a right of the poor over the wealthy, redistributing resources to eight prescribed categories of recipients.",
  },
  {
    id: "sadaqah", title: "Sadaqah", tag: "Voluntary Charity",
    image: img("1533222535026-754c501569dd"),
    text: "Sadaqah is voluntary charity that flows without limit or condition. It can be given at any time, in any amount, and takes countless forms — from feeding a family to a kind word or a smile. Unlike Zakat, there is no minimum and no fixed rate; the reward multiplies with sincerity.",
  },
  {
    id: "waqf", title: "Waqf", tag: "Perpetual Endowment",
    image: img("1512970648279-ff3398568f77"),
    text: "Waqf is a perpetual endowment in which the principal asset is preserved while its yield continually benefits the community. Historically, Waqf funded mosques, schools, hospitals and water systems for centuries. It is one of the most powerful forms of sustainable giving — a gift that keeps giving across generations.",
  },
  {
    id: "sadaqah-jariyah", title: "Sadaqah Jariyah", tag: "Continuous Charity",
    image: img("1760873059715-7c7cfbe2a2c6"),
    text: "Sadaqah Jariyah is ongoing charity whose reward continues long after the initial act — building a well, sponsoring a school, or planting a tree. As long as people benefit from it, the reward flows to the giver, even after their lifetime. It is an investment in both this world and the next.",
  },
  {
    id: "ramadan", title: "Ramadan Giving", tag: "The Blessed Month",
    image: img("1698047181077-d77608c3f85b"),
    text: "Ramadan multiplies the reward of every good deed. Giving during the blessed month includes Fitrana (Zakat al-Fitr) paid before Eid prayer, Fidya for those unable to fast, and Iftar provisions so that those in hardship can break their fast with dignity. It is a season of generosity and mercy.",
  },
  {
    id: "qurbani", title: "Qurbani", tag: "The Sacrifice of Eid",
    image: img("1677128912094-36d988ce198b"),
    text: "Qurbani commemorates the willingness of Prophet Ibrahim (AS) to sacrifice for the sake of Allah. During Eid al-Adha, an animal is sacrificed and fresh meat is shared with families in greatest need — for many, one of the few times in the year they receive quality protein. It embodies devotion and sharing.",
  },
];

const FAQS = [
  ["What is Zakat?", "Zakat is an obligatory form of giving in Islam, generally calculated as 2.5% of qualifying net wealth held for a lunar year above the Nisab threshold."],
  ["What is Nisab?", "Nisab is the minimum amount of wealth a Muslim must possess before Zakat is due. It can be based on the value of gold (85g) or silver (595g)."],
  ["When is Zakat due?", "Zakat becomes due once your qualifying wealth has remained above the Nisab threshold for one lunar year (hawl)."],
  ["What is the difference between Zakat and Sadaqah?", "Zakat is obligatory, fixed at 2.5% of qualifying wealth above Nisab. Sadaqah is voluntary, with no minimum, maximum or fixed rate."],
  ["Is this calculator a religious ruling?", "No. The calculator is for informational and educational purposes only. Please consult a qualified scholar for your personal circumstances."],
];

export default function IslamicGiving() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <PageHero
        label="Islamic Giving"
        title="Fulfil your obligation, multiply your reward"
        subtitle="From Zakat to Waqf, every form of giving is explained with clarity and handled with the utmost care, transparency and respect."
        image={img("1581141444721-0e6f8fa8397e")}
        crumbs={[{ label: "Islamic Giving" }]}
      />

      <section className="bg-white">
        <Container className="py-16">
          <div className="max-w-2xl">
            <SectionLabel>Understanding the forms of giving</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900">Every form of giving, explained</h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {TERMS.map((term) => (
              <div key={term.id} id={term.id} className="scroll-mt-28 overflow-hidden rounded-2xl border border-emerald-900/10 bg-white transition hover:-translate-y-1 hover:border-gold/40 hover:hh-shadow">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={term.image} alt={term.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 to-transparent" />
                  <span className="absolute start-4 bottom-3 text-xs font-semibold uppercase tracking-wider text-gold-light">{term.tag}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold text-emerald-900">{term.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{term.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="islamic-pattern text-white">
        <Container className="grid items-center gap-10 py-16 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <SectionLabel light>Zakat Calculator</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold">Calculate your Zakat in minutes</h2>
            <p className="mt-4 text-lg text-emerald-50/80">Multi-currency, with gold or silver Nisab. Download your calculation as a PDF. No payment is required to use the calculator.</p>
            <Link to="/zakat-calculator" data-testid="islamic-open-calculator" className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-white transition hover:bg-gold-light"><Calculator className="h-4 w-4" /> Open Zakat Calculator <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
          </div>
          {settings && (
            <div className="rounded-3xl border border-gold/20 bg-emerald-900/50 p-7 backdrop-blur">
              <div className="text-xs uppercase tracking-wider text-gold-light">Reference thresholds</div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-emerald-100/70">Gold Nisab</span><b>{settings.nisab_gold_grams}g</b></div>
                <div className="flex justify-between"><span className="text-emerald-100/70">Silver Nisab</span><b>{settings.nisab_silver_grams}g</b></div>
                <div className="flex justify-between"><span className="text-emerald-100/70">Zakat Rate</span><b>{(settings.zakat_rate * 100).toFixed(2)}%</b></div>
              </div>
              <p className="mt-4 text-xs text-emerald-100/60">Thresholds are configurable and updated by administrators. Consult a scholar for personal rulings.</p>
            </div>
          )}
        </Container>
      </section>

      <section id="faqs" className="scroll-mt-28 bg-sand">
        <Container className="py-16">
          <div className="mx-auto max-w-3xl">
            <div className="text-center">
              <SectionLabel>FAQs</SectionLabel>
              <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900">Islamic Giving FAQs</h2>
            </div>
            <Accordion type="single" collapsible className="mt-10">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="mb-3 rounded-xl border border-emerald-900/10 bg-white px-5">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-start font-semibold text-emerald-900 hover:no-underline"><span className="flex items-center gap-2"><HelpCircle className="h-4 w-4 text-gold" /> {q}</span></AccordionTrigger>
                  <AccordionContent className="text-slate-600">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Container>
      </section>
    </div>
  );
}
